/** Чистые функции публичных страниц афиши по городам (/afisha/<slug>). */
import { istanbulDay } from "@/lib/events-calendar";
import { isFreePrice } from "@/lib/registration-mode";
import type { AfishaCity } from "@/lib/afisha-cities";

const TZ = "Europe/Istanbul";

/** Минимум предстоящих событий, чтобы страница города индексировалась и попадала в sitemap. */
export const AFISHA_MIN_EVENTS = 10;

export function shouldIndexAfisha(upcomingCount: number): boolean {
  return upcomingCount >= AFISHA_MIN_EVENTS;
}

/** Часы и минуты по Стамбулу (UTC+3 круглый год). */
export function istanbulHm(iso: string): string {
  const d = new Date(new Date(iso).getTime() + 3 * 3600_000);
  return d.toISOString().slice(11, 16);
}

/** Только дата по Стамбулу: «12 ноября». */
export function afishaDayLabel(iso: string | null): string {
  if (!iso) return "Дата уточняется";
  return new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "long", timeZone: TZ });
}

/** «12 ноября · 19:00»; время 00:00 (не указано) не пишем — просто «12 ноября». */
export function afishaDateLabel(iso: string | null): string {
  if (!iso) return "Дата уточняется";
  const day = afishaDayLabel(iso);
  const hm = istanbulHm(iso);
  return hm === "00:00" ? day : `${day} · ${hm}`;
}

/** «4 ноября 2026» по Стамбулу — для строки «Обновлено». */
export function istanbulTodayLabel(now: Date = new Date()): string {
  return now.toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric", timeZone: TZ });
}

const WD = new Intl.DateTimeFormat("en-US", { timeZone: TZ, weekday: "short" });

/** Событие приходится на субботу или воскресенье по Стамбулу. */
export function isWeekendEvent(iso: string | null): boolean {
  if (!iso) return false;
  const w = WD.format(new Date(iso));
  return w === "Sat" || w === "Sun";
}

/** Событие сегодня по Стамбулу. */
export function isTodayEvent(iso: string | null, now: Date = new Date()): boolean {
  return !!iso && istanbulDay(iso) === istanbulDay(now);
}

/**
 * Цена для карточки: только если указана явно.
 * «Бесплатно» — только при явном 0/«бесплатно», иначе null (цену не показываем).
 */
export function afishaPriceLabel(price: string | null | undefined): string | null {
  const v = (price ?? "").trim();
  if (!v) return null;
  if (isFreePrice(v)) return "Бесплатно";
  const n = Number(v.replace(/\s/g, "").replace(",", "."));
  if (Number.isFinite(n)) return n === 0 ? "Бесплатно" : `${v.replace(/\s/g, "")} ₺`;
  return v;
}

/** Дата для JSON-LD: без реального времени (00:00) — только дата, иначе с +03:00. */
export function afishaSchemaDate(iso: string | null): string | undefined {
  if (!iso) return undefined;
  const d = new Date(new Date(iso).getTime() + 3 * 3600_000);
  const local = d.toISOString().slice(0, 19);
  return istanbulHm(iso) === "00:00" ? local.slice(0, 10) : `${local}+03:00`;
}

export type AfishaEventLike = {
  id: string;
  title: string;
  event_date: string | null;
  event_end_date?: string | null;
};

/** События, которые НАЧИНАЮТСЯ сегодня или позже (по Стамбулу). Идущие серии сюда не входят. */
export function upcomingEvents<T extends AfishaEventLike>(events: T[], now: Date = new Date()): T[] {
  const today = istanbulDay(now);
  return events.filter((e) => !!e.event_date && istanbulDay(e.event_date) >= today);
}

/** Многодневные серии, которые уже идут: начались раньше сегодня и ещё не закончились. */
export function ongoingEvents<T extends AfishaEventLike>(events: T[], now: Date = new Date()): T[] {
  const today = istanbulDay(now);
  return events.filter(
    (e) =>
      !!e.event_date &&
      !!e.event_end_date &&
      istanbulDay(e.event_date) < today &&
      new Date(e.event_end_date).getTime() >= now.getTime(),
  );
}

/** События на ближайшие субботу/воскресенье (начало >= сегодня, по Стамбулу). */
export function weekendEvents<T extends AfishaEventLike>(events: T[], now: Date = new Date()): T[] {
  const wk = upcomingEvents(events, now).filter((e) => isWeekendEvent(e.event_date));
  if (wk.length === 0) return [];
  const firstDay = wk.map((e) => istanbulDay(e.event_date!)).sort()[0];
  const nextDay = istanbulDay(new Date(Date.parse(`${firstDay}T00:00:00Z`) + 86_400_000));
  return wk.filter((e) => {
    const d = istanbulDay(e.event_date!);
    return d === firstDay || d === nextDay;
  });
}

/** Дата для карточки списка: идущая серия — «идёт до {дата}», иначе дата (и время, если указано). */
export function afishaCardDateLabel(
  e: { event_date: string | null; event_end_date?: string | null },
  now: Date = new Date(),
): string {
  if (
    e.event_date &&
    e.event_end_date &&
    istanbulDay(e.event_date) < istanbulDay(now) &&
    new Date(e.event_end_date).getTime() >= now.getTime()
  ) {
    return `идёт до ${afishaDayLabel(e.event_end_date)}`;
  }
  return afishaDateLabel(e.event_date);
}

/** «Сейчас 5 событий: ближайшее — 12 ноября, самое позднее — 30 ноября» (даты без времени). */
export function afishaCountPhrase(n: number, nearestIso: string | null, lastIso: string | null): string {
  return `Сейчас ${eventsCountLabel(n)}: ближайшее — ${afishaDayLabel(nearestIso)}, самое позднее — ${afishaDayLabel(lastIso)}.`;
}

/** JSON-LD ItemList из первых 20 событий. */
export function buildAfishaJsonLd(events: AfishaEventLike[], city: AfishaCity, baseUrl: string) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: events.slice(0, 20).map((e, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Event",
        name: e.title,
        ...(afishaSchemaDate(e.event_date) ? { startDate: afishaSchemaDate(e.event_date) } : {}),
        ...(afishaSchemaDate(e.event_end_date ?? null)
          ? { endDate: afishaSchemaDate(e.event_end_date ?? null) }
          : {}),
        location: {
          "@type": "Place",
          name: city.name,
          address: { "@type": "PostalAddress", addressLocality: city.name, addressCountry: "TR" },
        },
        url: `${baseUrl}/e/${e.id}`,
        eventStatus: "https://schema.org/EventScheduled",
      },
    })),
  };
}

/** Русская плюрализация: «1 событие», «3 события», «5 событий». */
export function eventsCountLabel(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} событие`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} события`;
  return `${n} событий`;
}
